import React, { useMemo, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  useTheme,
  Card,
  CardContent,
  alpha,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  Alert,
  CircularProgress,
  Collapse,
  Link,
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import { Email, Instagram, OpenInNew, Send, InfoOutlined, ExpandMore } from '@mui/icons-material';
import { ContentUseCase } from '../../corelogic/usecases/ContentUseCase';
import { useLanguage } from '../../shared/contexts/LanguageContext';
import { SEO } from './SEO';
import { BackToTop } from './BackToTop';
import { trackSocialClick } from '../../configuration/analytics';

const contentUseCase = new ContentUseCase();

interface FormData {
  name: string;
  email: string;
  phone: string;
  address: string;
  run: string;
  runDate: string;
  size: string;
  howHeard: string;
  notes: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  address?: string;
  size?: string;
}

const encode = (data: Record<string, string>) =>
  Object.keys(data)
    .map(k => `${encodeURIComponent(k)}=${encodeURIComponent(data[k])}`)
    .join('&');

export const MitmachenPage: React.FC = () => {
  const theme = useTheme();
  const { language } = useLanguage();
  const { mitmachen, socialMedia } = contentUseCase.getAllContent(language);
  const form = mitmachen.form;

  const [formData, setFormData] = useState<FormData>({
    name: '', email: '', phone: '', address: '', run: '', runDate: '', size: '', howHeard: '', notes: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [infoExpanded, setInfoExpanded] = useState(false);
  const [botField] = useState(''); // honeypot: keep empty

  const info = useMemo(() => {
    const first = form.infoText?.[0] ?? '';
    const rest = (form.infoText ?? []).slice(1);
    return { first, rest };
  }, [form.infoText]);

  const sizeHasLongerDelivery = (form.outOfStock as string[])?.includes(formData.size) ?? false;

  const handleChange = (field: keyof FormData) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFormData(prev => ({ ...prev, [field]: e.target.value }));
      if (errors[field as keyof FormErrors]) {
        setErrors(prev => ({ ...prev, [field]: undefined }));
      }
    };

  const handleSelectChange = (e: SelectChangeEvent) => {
    setFormData(prev => ({ ...prev, size: e.target.value }));
    if (errors.size) setErrors(prev => ({ ...prev, size: undefined }));
  };

  const validate = (): boolean => {
    const next: FormErrors = {};
    if (!formData.name.trim()) next.name = form.errors.required;
    if (!formData.email.trim() || !/\S+@\S+\.\S+/.test(formData.email))
      next.email = form.errors.email;
    if (!formData.address.trim()) next.address = form.errors.required;
    if (!formData.size) next.size = form.errors.required;
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setStatus('submitting');
    try {
      await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: encode({
          'form-name': 'registration',
          'bot-field': botField,
          name: formData.name,
          email: formData.email,
          phone: formData.phone || '—',
          address: formData.address,
          run: formData.run || '—',
          runDate: formData.runDate || '—',
          size: formData.size,
          howHeard: formData.howHeard || '—',
          notes: formData.notes || '—',
          language: language.toUpperCase(),
        }),
      });
      setStatus('success');
      setFormData({ name: '', email: '', phone: '', address: '', run: '', runDate: '', size: '', howHeard: '', notes: '' });
    } catch {
      setStatus('error');
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 4, md: 8 } }}>
      <SEO page="mitmachen" />

      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
        <Typography
          variant="h2"
          component="h1"
          gutterBottom
          sx={{
            color: 'text.primary',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          {mitmachen.title}
          <Box
            component="img"
            src="/Light_blue_ribbon.svg.png"
            alt={language === 'de' ? 'Hellblaues Bewusstseinsband' : 'Light Blue Awareness Ribbon'}
            sx={{
              height: { xs: 40, md: 56 },
              width: 'auto',
            }}
          />
        </Typography>
      </Box>

      {/* How it works + Registration form */}
      <Box sx={{ maxWidth: { xs: '100%', md: 960 }, mx: 'auto', mb: { xs: 6, md: 8 } }}>

        {/* Info card */}
        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            border: `1px solid ${theme.palette.divider}`,
            mb: 4,
            background: alpha(theme.palette.primary.main, 0.03),
          }}
        >
          <CardContent sx={{ p: { xs: 3, sm: 4, md: 5 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
              <InfoOutlined sx={{ color: 'primary.main' }} />
              <Typography variant="h6" fontWeight={700}>
                {form.infoTitle}
              </Typography>
            </Box>
            {(() => {
              const paraSx = {
                mb: 1.5,
                lineHeight: 1.75,
                fontSize: { xs: '1rem', md: '1.0625rem' },
              } as const;

              return (
                <>
                  {!!info.first && (
              <Typography
                variant="body1"
                color="text.secondary"
                sx={paraSx}
              >
                {info.first}
              </Typography>
            )}

            {info.rest.length > 0 && (
              <>
                <Collapse in={infoExpanded} timeout={200} unmountOnExit>
                  <Box sx={{ mt: 0.5 }}>
                    {info.rest.map((para: string) => (
                      <Typography
                        key={para.slice(0, 40)}
                        variant="body1"
                        color="text.secondary"
                        sx={paraSx}
                      >
                        {para}
                      </Typography>
                    ))}
                  </Box>
                </Collapse>
                <Button
                  variant="text"
                  onClick={() => setInfoExpanded(v => !v)}
                  endIcon={
                    <ExpandMore
                      sx={{
                        transform: infoExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 150ms ease',
                      }}
                    />
                  }
                  sx={{
                    mt: 1,
                    px: 0,
                    minWidth: 0,
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: 2,
                  }}
                >
                  {infoExpanded ? form.infoShowLess : form.infoShowMore}
                </Button>
              </>
            )}
                </>
              );
            })()}
            <Box
              sx={{
                mt: 2.5,
                pt: 2,
                borderTop: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Typography variant="body2" fontWeight={600} color="primary.main">
                {form.paymentInfo}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {form.material}
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Registration form */}
        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            border: `1px solid ${theme.palette.divider}`,
            overflow: 'hidden',
            transition: 'all 0.25s ease',
            '&:hover': {
              borderColor: theme.palette.primary.main,
              boxShadow: `0 12px 40px ${alpha(theme.palette.primary.main, 0.12)}`,
            },
          }}
        >
          <CardContent sx={{ p: { xs: 2, sm: 4, md: 5 } }}>
            <Typography
              variant="h5"
              fontWeight={700}
              gutterBottom
              sx={{
                mb: { xs: 2, sm: 3 },
                fontSize: { xs: '1.25rem', md: '1.5rem' },
              }}
            >
              {form.title}
            </Typography>

            {status === 'success' ? (
              <Alert severity="success" sx={{ borderRadius: 2 }}>
                <Typography fontWeight={600}>{form.success.title}</Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>{form.success.text}</Typography>
              </Alert>
            ) : (
              <Box
                component="form"
                onSubmit={handleSubmit}
                noValidate
                data-netlify="true"
                data-netlify-honeypot="bot-field"
                name="registration"
              >
                <input type="hidden" name="form-name" value="registration" />
                <input type="hidden" name="bot-field" value={botField} />
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                    gap: { xs: 2, sm: 2.5 },
                  }}
                >
                  <TextField
                    label={form.fields.name}
                    name="name"
                    value={formData.name}
                    onChange={handleChange('name')}
                    error={!!errors.name}
                    helperText={errors.name}
                    required
                    fullWidth
                    size="small"
                  />
                  <TextField
                    label={form.fields.email}
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange('email')}
                    error={!!errors.email}
                    helperText={errors.email}
                    required
                    fullWidth
                    size="small"
                  />
                  <TextField
                    label={form.fields.phone}
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange('phone')}
                    fullWidth
                    sx={{ gridColumn: { sm: '1 / -1' } }}
                    size="small"
                  />
                  <TextField
                    label={form.fields.address}
                    name="address"
                    value={formData.address}
                    onChange={handleChange('address')}
                    error={!!errors.address}
                    helperText={errors.address}
                    required
                    fullWidth
                    multiline
                    rows={2}
                    sx={{ gridColumn: { sm: '1 / -1' } }}
                    size="small"
                  />
                  <TextField
                    label={form.fields.run}
                    name="run"
                    value={formData.run}
                    onChange={handleChange('run')}
                    fullWidth
                    sx={{ gridColumn: { sm: '1 / -1' } }}
                    size="small"
                  />
                  <TextField
                    label={form.fields.runDate}
                    name="runDate"
                    value={formData.runDate}
                    onChange={handleChange('runDate')}
                    fullWidth
                    sx={{ gridColumn: { sm: '1 / -1' } }}
                    size="small"
                  />
                  <TextField
                    label={form.fields.howHeard}
                    name="howHeard"
                    value={formData.howHeard}
                    onChange={handleChange('howHeard')}
                    fullWidth
                    sx={{ gridColumn: { sm: '1 / -1' } }}
                    size="small"
                  />
                  <TextField
                    label={form.fields.notes}
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange('notes')}
                    fullWidth
                    multiline
                    rows={3}
                    sx={{ gridColumn: { sm: '1 / -1' } }}
                    size="small"
                  />
                  <FormControl fullWidth error={!!errors.size} required size="small">
                    <InputLabel
                      id="tshirt-size-label"
                      sx={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: '100%',
                      }}
                    >
                      {form.fields.size}
                    </InputLabel>
                    <Select
                      id="tshirt-size-select"
                      labelId="tshirt-size-label"
                      value={formData.size}
                      name="size"
                      label={form.fields.size}
                      onChange={handleSelectChange}
                      size="small"
                      sx={{
                        '& .MuiSelect-select': {
                          display: 'flex',
                          alignItems: 'center',
                        },
                      }}
                    >
                      {form.sizeOptions.map((s: string) => {
                        const unavailable = (form.outOfStock as string[])?.includes(s) ?? false;
                        return (
                          <MenuItem key={s} value={s}>
                            {s}
                            {unavailable && (
                              <Typography
                                component="span"
                                variant="caption"
                                color="text.disabled"
                                sx={{ ml: 1 }}
                              >
                                ({form.outOfStockLabel})
                              </Typography>
                            )}
                          </MenuItem>
                        );
                      })}
                    </Select>
                    {errors.size ? (
                      <FormHelperText>{errors.size}</FormHelperText>
                    ) : sizeHasLongerDelivery ? (
                      <FormHelperText>{form.outOfStockHelper}</FormHelperText>
                    ) : null}
                  </FormControl>
                </Box>

                {status === 'error' && (
                  <Alert severity="error" sx={{ mt: 2.5, borderRadius: 2 }}>
                    {form.error}
                  </Alert>
                )}

                <Box
                  sx={{
                    mt: 3,
                    p: 2,
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.divider}`,
                    backgroundColor: 'grey.50',
                  }}
                >
                  <Typography variant="body2" fontWeight={600} gutterBottom>
                    {form.withdrawal.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {form.withdrawal.text}
                  </Typography>
                  <Link
                    href="/impressum"
                    color="primary"
                    underline="hover"
                    variant="body2"
                    sx={{ display: 'inline-block', mt: 1 }}
                  >
                    {form.withdrawal.linkText}
                  </Link>
                </Box>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ display: 'block', mt: 2.5 }}
                >
                  {form.submitHint}
                </Typography>

                <Button
                  type="submit"
                  variant="contained"
                  disabled={status === 'submitting'}
                  startIcon={
                    status === 'submitting'
                      ? <CircularProgress size={18} color="inherit" />
                      : <Send />
                  }
                  sx={{
                    mt: 1.5,
                    borderRadius: 2,
                    px: 4,
                    py: 1.5,
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '1rem',
                  }}
                >
                  {status === 'submitting' ? form.submitting : form.submit}
                </Button>
                {form.consentText && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mt: 1.5 }}
                  >
                    {form.consentText}{' '}
                    <Link href="/privacy" color="primary" underline="hover">
                      {form.consentLinkText}
                    </Link>
                  </Typography>
                )}
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* Contact cards */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 3,
          maxWidth: { xs: '100%', md: 960 },
          mx: 'auto',
        }}
      >
        {/* Email card */}
        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            border: `1px solid ${theme.palette.divider}`,
            overflow: 'hidden',
            transition: 'all 0.25s ease',
            '&:hover': {
              borderColor: theme.palette.primary.main,
              boxShadow: `0 12px 40px ${alpha(theme.palette.primary.main, 0.12)}`,
            },
          }}
        >
          <CardContent sx={{ p: { xs: 3, sm: 4, md: 5 } }}>
            <Box
              sx={{
                width: 48, height: 48, borderRadius: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2,
              }}
            >
              <Email sx={{ fontSize: 28, color: 'primary.main' }} />
            </Box>
            <Typography variant="h5" fontWeight={700} gutterBottom>
              {mitmachen.email.label}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              {mitmachen.email.description}
            </Typography>
            <Button
              variant="contained"
              href={`mailto:${mitmachen.email.mailto}?subject=${encodeURIComponent(
                language === 'de' ? 'Mitmachen bei Run for M.E.' : 'Join Run for M.E.'
              )}`}
              startIcon={<Email />}
              sx={{
                borderRadius: 2, px: 3, py: 1.5,
                textTransform: 'none', fontWeight: 600,
              }}
            >
              {mitmachen.email.address.replace('(at)', '@')}
            </Button>
          </CardContent>
        </Card>

        {/* Instagram card */}
        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            border: `1px solid ${theme.palette.divider}`,
            overflow: 'hidden',
            transition: 'all 0.25s ease',
            '&:hover': {
              borderColor: theme.palette.primary.main,
              boxShadow: `0 12px 40px ${alpha(theme.palette.primary.main, 0.12)}`,
            },
          }}
        >
          <CardContent sx={{ p: { xs: 3, sm: 4, md: 5 } }}>
            <Box
              sx={{
                width: 48, height: 48, borderRadius: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2,
              }}
            >
              <Instagram sx={{ fontSize: 28, color: 'primary.main' }} />
            </Box>
            <Typography variant="h5" fontWeight={700} gutterBottom>
              {mitmachen.instagram.title}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              {mitmachen.instagram.description}
            </Typography>
            <Button
              variant="outlined"
              href={socialMedia.instagram.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackSocialClick('instagram_page')}
              startIcon={<OpenInNew />}
              sx={{
                borderRadius: 2, px: 3, py: 1.5,
                textTransform: 'none', fontWeight: 600,
                borderWidth: 2, '&:hover': { borderWidth: 2 },
              }}
            >
              {mitmachen.instagram.button}
            </Button>
          </CardContent>
        </Card>
      </Box>

      <Box sx={{ mt: 6, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {language === 'de'
            ? 'Wir freuen uns auf deine Nachricht – ob für einen Lauf, eine Kooperation oder ein einfaches Hallo.'
            : 'We look forward to hearing from you – whether for a run, a partnership, or just to say hello.'}
        </Typography>
      </Box>
      <BackToTop />
    </Container>
  );
};
